import { Extension } from '@tiptap/core'
import { TextSelection } from '@tiptap/pm/state'

import { gridStateActions } from '@/features/rundown/grid/grid.store'

import type { Node as PMNode } from '@tiptap/pm/model'
import type { EditorState, Transaction } from '@tiptap/pm/state'

// Declare custom commands so TS recognizes them globally
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customShortcuts: {
      /** Toggles the selected text case: UPPERCASE -> lowercase -> Title Case */
      toggleCase: () => ReturnType
    }
  }
}

export const CustomShortcutsExtension = Extension.create({
  name: 'customShortcuts',

  addCommands() {
    return {
      toggleCase:
        () =>
        ({ state, dispatch }) => {
          return runToggleCase(state, dispatch)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Save with Mod+Enter
      'Mod-Enter': ({ editor }) => {
        if (isSuggestionsActive(editor)) {
          return false // Let active extension handle it
        }

        gridStateActions.editor.finishEditing('explicit')
        return true
      },

      // Discard with Escape
      Escape: ({ editor }) => {
        if (isSuggestionsActive(editor)) {
          return false // Let active extension handle it
        }

        gridStateActions.editor.discard()
        return true
      },

      // TODO: Temp. handler to prevent focus trapping by broken focus management on cell title
      'Shift-Tab': ({ editor }) => {
        if (isSuggestionsActive(editor)) {
          return false // Let active extension handle it
        }

        // Find parent cell
        const cellElm =
          editor.view.dom.parentElement?.parentElement?.parentElement

        // Focus first button in toolbar
        const toolbarElm = cellElm?.querySelector('[role="toolbar"] button')
        if (!(toolbarElm instanceof HTMLElement)) return false
        toolbarElm.focus({ preventScroll: true })

        return true
      },

      // Clear Formatting
      'Mod-\\': () =>
        this.editor.chain().focus().clearNodes().unsetAllMarks().run(),

      // Toggle case
      'Mod-Shift-U': () => this.editor.commands.toggleCase(),

      // Insert divider
      'Mod-Shift--': () => this.editor.commands.setHorizontalRule(),
    }
  },
})

/**
 * Helper to check if any suggestions plugin is active
 */
function isSuggestionsActive(editor) {
  return editor.state.plugins.some((plugin) => {
    const pluginState = plugin.getState(editor.state)
    return pluginState?.active === true
  })
}

/**
 * Logic for toggling case of nodes
 */
function runToggleCase(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
): boolean {
  const { selection, tr } = state
  const { from, to, empty } = selection

  if (empty) return false

  const overallText = state.doc.textBetween(from, to, ' ')
  let globalTargetCase: 'upper' | 'lower' | 'title' = 'upper'

  if (overallText === overallText.toUpperCase()) {
    globalTargetCase = 'lower'
  } else if (overallText === overallText.toLowerCase()) {
    globalTargetCase = 'title'
  }

  // Tiptap dry-run check
  if (!dispatch) return true

  let offset = 0

  state.doc.nodesBetween(from, to, (node: PMNode, pos: number) => {
    if (!node.isText || !node.text) return true

    const nodeFrom = Math.max(from, pos)
    const nodeTo = Math.min(to, pos + node.text.length)

    if (nodeFrom >= nodeTo) return true

    const originalSnippet = node.text.slice(nodeFrom - pos, nodeTo - pos)
    let transformedSnippet = ''

    if (globalTargetCase === 'lower') {
      transformedSnippet = originalSnippet.toLowerCase()
    } else if (globalTargetCase === 'title') {
      transformedSnippet = originalSnippet.replace(/\b\w/g, (char) =>
        char.toUpperCase(),
      )
    } else {
      transformedSnippet = originalSnippet.toUpperCase()
    }

    const targetFrom = nodeFrom + offset
    const targetTo = nodeTo + offset

    tr.insertText(transformedSnippet, targetFrom, targetTo)
    tr.ensureMarks(node.marks)

    offset += transformedSnippet.length - originalSnippet.length
    return true
  })

  tr.setSelection(TextSelection.create(tr.doc, from, to + offset))
  dispatch(tr)

  return true
}
